import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextDto } from '@/types/dtos/live-contexts/live-context.dto';
import { getObjectId } from '@/helpers/object.id.helper';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { getAppQueryClient } from '@/state/query-client-holder';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_LIVE_CONTEXT_STATE_QUERY_KEY } from '@/state/queries/player/use-get-live-context-state';
import { entityRoomKeyFromEntityRef, resetPerformControlRoom } from '@/state/player/perform-control-ws.store';
import { resetLivePlayStateRoom } from '@/state/player/live-play-state-ws.store';

function flowMatchesLiveEntity(flow: FlowDto | undefined, entityId: string): boolean {
  if (!flow) return false;
  if (flow.id === entityId) return true;
  if (flow.liveBindingFlowId === entityId) return true;
  if (flow.liveContext?.entityId === entityId) return true;
  const originId = flow.origin ? getObjectId(flow.origin) : undefined;
  return originId === entityId;
}

function patchFlowIsRunning(flow: FlowDto | undefined, entityId: string, isRunning: boolean): FlowDto | undefined {
  if (!flow || !flowMatchesLiveEntity(flow, entityId)) return flow;

  const nextLiveContext: LiveContextDto = flow.liveContext
    ? {
        ...flow.liveContext,
        entityId: flow.liveContext.entityId || entityId,
        isRunning,
        updatedAt: new Date(),
      }
    : {
        id: entityId,
        entityType: 'Flow',
        entityId,
        isRunning,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

  return { ...flow, liveContext: nextLiveContext };
}

function patchQueryFlowCache(entityId: string, isRunning: boolean): void {
  const queryClient = getAppQueryClient();
  if (!queryClient) return;

  queryClient.setQueriesData({ queryKey: [GET_FLOW_QUERY_KEY] }, (cached) => {
    if (!cached) return cached;
    if (Array.isArray(cached)) {
      return cached.map((flow) => patchFlowIsRunning(flow as FlowDto, entityId, isRunning) ?? flow);
    }
    return patchFlowIsRunning(cached as FlowDto, entityId, isRunning) ?? cached;
  });
}

/** Live-context query and WS rooms are keyed by the binding flow, not the PLAN origin. */
function bindingEntityRefsForFlow(flow: FlowDto, entityType: string): string[] {
  const refs: string[] = [];
  if (flow.liveContext?.entityType && flow.liveContext.entityId) {
    refs.push(`${flow.liveContext.entityType}:${flow.liveContext.entityId}`);
  }
  if (flow.liveBindingFlowId) {
    refs.push(`${entityType}:${flow.liveBindingFlowId}`);
  }
  return refs;
}

/**
 * The go-live invalidation fires before auto-cue has written the new playout.
 * Drop the previous session so Control does not paint that item as current.
 * Invalidations often name the origin id; also clear the matched flow's binding ref.
 */
function dropPreviousLiveSessionCaches(entityType: string, entityId: string): void {
  if (entityType !== 'Flow' && entityType !== 'Event') return;
  const entityRefs = new Set<string>([`${entityType}:${entityId}`]);
  const store = useFlowStore.getState();
  for (const flow of [store.flow, store.flowPlan, store.flowOrganize]) {
    if (!flow || !flowMatchesLiveEntity(flow, entityId)) continue;
    for (const entityRef of bindingEntityRefsForFlow(flow, entityType)) {
      entityRefs.add(entityRef);
    }
  }

  const queryClient = getAppQueryClient();
  for (const entityRef of entityRefs) {
    queryClient?.removeQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
    const roomKey = entityRoomKeyFromEntityRef(entityRef);
    if (!roomKey) continue;
    resetLivePlayStateRoom(roomKey);
    resetPerformControlRoom(roomKey);
  }
}

/**
 * Apply an authoritative `isRunning` flip from `live-context-invalidated` so all open
 * perform/control tabs update immediately without waiting on GET_FLOW refetch.
 */
export function applyLiveContextRunningFromWs(
  entityId: string,
  isRunning: boolean,
  entityType: 'Flow' | 'Event' = 'Flow',
): void {
  dropPreviousLiveSessionCaches(entityType, entityId);
  const store = useFlowStore.getState();
  const nextFlow = patchFlowIsRunning(store.flow, entityId, isRunning);
  const nextFlowPlan = patchFlowIsRunning(store.flowPlan, entityId, isRunning);
  const nextFlowOrganize = patchFlowIsRunning(store.flowOrganize, entityId, isRunning);

  const patch: Partial<{ flow: FlowDto; flowPlan: FlowDto; flowOrganize: FlowDto }> = {};
  if (nextFlow && nextFlow !== store.flow) patch.flow = nextFlow;
  if (nextFlowPlan && nextFlowPlan !== store.flowPlan) patch.flowPlan = nextFlowPlan;
  if (nextFlowOrganize && nextFlowOrganize !== store.flowOrganize) patch.flowOrganize = nextFlowOrganize;
  if (Object.keys(patch).length > 0) {
    useFlowStore.setState(patch);
  }

  // Keep React Query GET_FLOW cache in sync — Control header passes query `flow` into the switch.
  patchQueryFlowCache(entityId, isRunning);
}
